import type { TouchDto, TouchesPageDto } from '@glory/contracts';
import { formatDate, formatDayMonth, t } from '@glory/i18n';
import { isoDateIn, zonedInstant } from '@glory/kernel';
import {
  clockMinutes,
  isIsoDate,
  TOUCH_DIRECTIONS,
  TOUCH_KINDS,
  type TouchDirection,
  type TouchKind,
} from '@glory/sales/domain';
import type { ChoiceOption } from '@glory/ui';

import { studioDateTime } from './steps.ts';

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

const flag = (value: unknown): true | undefined => (value === true || value === 'true' ? true : undefined);

const day = (value: unknown): string | undefined => {
  const raw = text(value);
  return raw !== undefined && isIsoDate(raw) ? raw : undefined;
};

/** «Касания» живут в адресе: кто, вид и период — ссылкой можно поделиться. */
export interface TouchesSearch {
  /** Кто записал: идентификатор сотрудника; без значения — все, кого видите. */
  readonly who?: string | undefined;
  readonly kind?: TouchKind | undefined;
  /** Период, дни студии включительно. */
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  /** Открыть «Записать звонок» — команда ⌘K; отметка из адреса убирается сразу. */
  readonly call?: true | undefined;
}

export function parseTouchesSearch(search: Record<string, unknown>): TouchesSearch {
  return {
    who: text(search.who),
    kind: TOUCH_KINDS.find((kind) => kind === search.kind),
    from: day(search.from),
    to: day(search.to),
    call: flag(search.call),
  };
}

export const touchKindLabel = (kind: TouchKind): string => t(`sales.touchKinds.${kind}`);
export const touchDirectionLabel = (direction: TouchDirection): string =>
  t(`sales.touchDirections.${direction}`);

export const touchKindOptions = (): ChoiceOption<TouchKind>[] =>
  TOUCH_KINDS.map((kind) => ({ value: kind, label: touchKindLabel(kind) }));

export const touchDirectionOptions = (): ChoiceOption<TouchDirection>[] =>
  TOUCH_DIRECTIONS.map((direction) => ({ value: direction, label: touchDirectionLabel(direction) }));

// --- время касания --------------------------------------------------------------------------------------

/** Момент из поля «дата и время» (`ГГГГ-ММ-ДДTЧЧ:ММ`, местное время студии) в точное время. */
export function instantOf(value: string, timeZone: string): Date {
  const minutes = clockMinutes(value.slice(11, 16)) ?? 0;
  return zonedInstant(timeZone, value.slice(0, 10), minutes);
}

/** Момент касания в поле «дата и время» по поясу студии. */
export const fieldValueOf = (at: Date, timeZone: string): string => studioDateTime(timeZone, at);

/** Касание из списка после удаления: журнал и счётчик меняются сразу. */
export function withoutTouch(page: TouchesPageDto, id: string): TouchesPageDto {
  if (!page.items.some((item) => item.id === id)) return page;
  return { ...page, items: page.items.filter((item) => item.id !== id), total: Math.max(0, page.total - 1) };
}

/** День журнала: подпись («Сегодня», «Вчера», «28 сентября») и касания этого дня, новые сверху. */
export interface TouchDay {
  readonly day: string;
  readonly label: string;
  readonly items: readonly TouchDto[];
}

const DAY_MS = 24 * 60 * 60_000;

/**
 * Касания по дням в поясе студии (карта взаимодействия, 8): длинная лента читается по датам, а не одной
 * простынёй. Порядок внутри — как пришёл с сервера (новые сверху).
 */
export function touchDays(items: readonly TouchDto[], timeZone: string, now: Date = new Date()): TouchDay[] {
  const today = isoDateIn(timeZone, now);
  const yesterday = isoDateIn(timeZone, new Date(now.getTime() - DAY_MS));
  const days: { day: string; items: TouchDto[] }[] = [];
  for (const touch of items) {
    const key = isoDateIn(timeZone, new Date(touch.at));
    const last = days.at(-1);
    if (last?.day === key) last.items.push(touch);
    else days.push({ day: key, items: [touch] });
  }
  return days.map(({ day: key, items: own }) => ({
    day: key,
    label:
      key === today
        ? t('sales.touchLog.today')
        : key === yesterday
          ? t('sales.touchLog.yesterday')
          : key.slice(0, 4) === today.slice(0, 4)
            ? formatDayMonth(key)
            : formatDate(key),
    items: own,
  }));
}

/** Время касания в поясе студии: «14:30». */
export const touchTime = (at: Date | string, timeZone: string): string =>
  studioDateTime(timeZone, new Date(at)).slice(11, 16);
